import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import FooterNavigation from "EmployeeApplication/FooterNavigation";
import { useGetEmployeeHealthAccountQuery, useUpdateEmployeeHealthAccountMutation } from "Features/EmployeeHealthAccounts";
import { Form } from "react-final-form";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import formatCurrency from "Utils/formatCurrency";
import { composeValidators, validateNumber, validateRequired, validateZero, validateMaxDollar } from "Utils/Validations";
import BasicLoading from "ZeroRuby/BasicLoading";

import { useGetApplicationQuery, useGetHealthAccountBenefitOverviewQuery } from "../api";
import Page from "../Page";
import BodyText, { Body1, Body2 } from "Cobalt/BodyText";
import { CardHeader, CardSection } from "Cobalt/Card";
import Header from "Cobalt/Header";
import { Popover } from "Cobalt/Popover";
import { SIFinalFormField } from "Common/forms";
import { RoutedBackButton } from "Common/forms/buttons";
import { Row } from "Common/Grid";
import { Li, Ul } from "Common/HTML";
import Icon from "Common/Icon";
import SIOverlay from "Common/SIOverlay";

const useData = () => {
  const fallbackEmpoyeeApplication = {
    employeeFormId: null,
    employeePolicy: {},
  };
  const { isLoading: isLoadingApplication, data: { employeePolicy } = fallbackEmpoyeeApplication } = useGetApplicationQuery();
  const { id: employeePolicyID } = employeePolicy;
  const { isLoading: isLoadingHealthAccountBenefitOverview, data: { healthAccountBenefitOverview } = { healthAccountBenefitOverview: null } } =
    useGetHealthAccountBenefitOverviewQuery({ id: employeePolicyID }, { skip: !employeePolicyID });
  const { isLoading: isLoadingEmployeeHealthAccount, data: { employeeHealthAccount } = { employeeHealthAccount: null } } = useGetEmployeeHealthAccountQuery(
    { employeePolicyID },
    { skip: !employeePolicyID },
  );

  return {
    employeeHealthAccount,
    healthAccountBenefitOverview,
    employeePolicyID,
    isLoading: isLoadingApplication || isLoadingHealthAccountBenefitOverview || isLoadingEmployeeHealthAccount,
  };
};

const PageContent = ({ backLink, onClickBack }) => {
  const { employeeHealthAccount, healthAccountBenefitOverview, employeePolicyID, isLoading } = useData();
  const hsaData = employeeHealthAccount?.enrollmentData;
  const [updateEmployeeHealthAccount] = useUpdateEmployeeHealthAccountMutation();
  const dispatch = useDispatch();

  if (isLoading) {
    return (
      <>
        <CardSection>
          <BasicLoading />
        </CardSection>
        <CardSection>
          <RoutedBackButton href={backLink} onClick={onClickBack} />
        </CardSection>
      </>
    );
  }

  const { employerHealthAccountTypeFormatted, employerYearlyContributionAmount, yearlyMaximums } = healthAccountBenefitOverview;
  const employerContributing = employerYearlyContributionAmount > 0;
  // const maskedAccountNumber = employeeHealthAccount?.maskedAccountNumber;
  const yearlyContributionAmount = employeeHealthAccount?.yearlyContributionAmount;

  const onSubmit = async (values) => {
    const res = await updateEmployeeHealthAccount({ employeePolicyID, employeeHealthAccount: values });
    if (res.error) {
      const { errors } = res.error.data;
      return errors;
    }
    dispatch(stepCompleted("healthAccountInformation"));
    return null;
  };
  return (
    <Form onSubmit={onSubmit} initialValues={{ yearlyContributionAmount: yearlyContributionAmount || 0 }}>
      {({ handleSubmit, invalid, submitting }) => (
        <form onSubmit={handleSubmit}>
          <CardHeader>{employerHealthAccountTypeFormatted} Contributions</CardHeader>
          <CardSection>
            {employerContributing && (
              <Header level={7} mb={4}>
                Your employer will contribute {formatCurrency(employerYearlyContributionAmount)} per year towards your {employerHealthAccountTypeFormatted}.
              </Header>
            )}
            <Body1>The maximums that can be contributed {employerContributing ? "between you and your employer " : ""}for the year are:</Body1>
            <Body1 py={2}>
              <Ul>
                <Li>Individual: {formatCurrency(yearlyMaximums.individual)}</Li>
                {yearlyMaximums.family && (
                  <Li>
                    <SIOverlay
                      placement="top"
                      overlayContent={
                        <Popover showBackdrop={false}>The family maximum applies if you have dependents enrolled in your HSA eligible insurance plan.</Popover>
                      }
                      overlayTarget={
                        <span>
                          Family: {formatCurrency(yearlyMaximums.family)}
                          <Icon ml={2} size="xs" icon="question-circle" />
                        </span>
                      }
                    />
                  </Li>
                )}
              </Ul>
            </Body1>
            {employerContributing && (
              <Body1 my={3}>
                After the employer contribution, the maximum you can contribute is {formatCurrency(hsaData?.employee_maximum)} total per year.
              </Body1>
            )}
          </CardSection>
          <CardHeader>Your Annual Contribution (Optional)</CardHeader>
          <CardSection>
            <Body1 level={3} mb={4}>
              You can contribute an annual amount to your {employerHealthAccountTypeFormatted}.
            </Body1>
            <Body1 mb={4}>This amount will be evenly distributed across your paychecks for the rest of the year.</Body1>
            <Row>
              <SIFinalFormField
                name="yearlyContributionAmount"
                label="Annual Contribution"
                componentProps={{
                  prependContent: "$",
                  type: "number",
                  min: 0,
                }}
                sm={6}
                validate={composeValidators(validateZero, validateNumber, validateRequired, validateMaxDollar(hsaData.employee_maximum))}
              />
            </Row>
          </CardSection>
          {/* Not using now, likely using in near future */}
          {/* <CardHeader>Account Number</CardHeader>
          <CardSection>
            <Header level={3} mb={4}>
              Fill out the Account Number of your {employerHealthAccountTypeFormatted} below to receive these benefits.
            </Header>
            <Row>
              <SIFinalFormField
                name="accountNumber"
                label="Account Number"
                sm={6}
                componentProps={{ ...(maskedAccountNumber ? { placeholder: maskedAccountNumber } : {}) }}
              />
            </Row>
          </CardSection> */}
          <FooterNavigation backLink={backLink} onClickBack={onClickBack} saveDisabled={invalid} submitting={submitting} />
        </form>
      )}
    </Form>
  );
};

const HealthAccountCoveragePage = ({ backLink, onClickBack }) => (
  <Page title="Choose Life Coverage">
    <PageContent backLink={backLink} onClickBack={onClickBack} />
  </Page>
);

export default HealthAccountCoveragePage;
